import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { HeartIcon } from '@heroicons/react/24/outline';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  max-width: 800px;
`;

const Section = styled.div`
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid ${theme.colors.border.primary};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: start;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: ${theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
  font-weight: 600;
`;

const SectionDescription = styled.p`
  color: ${theme.colors.text.secondary};
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
`;

const SettingItem = styled.div`
  padding: 1.5rem;
  background: ${theme.colors.background.tertiary};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 1rem;
  border: 1px solid ${theme.colors.border.primary};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${(p) => (p.$hasDescription ? '0.75rem' : '0')};
`;

const SettingLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${theme.colors.text.primary};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${theme.colors.primary.main};
  }
`;

const SettingDescription = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.5;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 3.5rem;
  height: 2rem;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(p) => (p.$checked ? theme.colors.primary.main : theme.colors.background.primary)};
    border-radius: ${theme.borderRadius.full};
    transition: all 0.3s ease;
    border: 2px solid ${(p) => (p.$checked ? theme.colors.primary.main : theme.colors.border.primary)};

    &:before {
      content: '';
      position: absolute;
      height: 1.5rem;
      width: 1.5rem;
      left: 0.125rem;
      bottom: 0.125rem;
      background: white;
      border-radius: 50%;
      transition: all 0.3s ease;
      transform: ${(p) => (p.$checked ? 'translateX(1.5rem)' : 'translateX(0)')};
    }
  }
`;

const Select = styled.select`
  padding: 0.5rem 1rem;
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 1rem;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: 0.875rem 2rem;
  background: ${(p) => (p.variant === 'primary' ? theme.colors.primary.main : theme.colors.background.tertiary)};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;

  &:hover {
    background: ${(p) => (p.variant === 'primary' ? '#2563eb' : theme.colors.background.primary)};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const InfoBox = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  margin-top: 1.5rem;

  h4 {
    color: ${theme.colors.primary.main};
    margin: 0 0 0.5rem 0;
    font-size: 0.9375rem;
    font-weight: 600;
  }

  p {
    color: ${theme.colors.text.secondary};
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const VesselCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  cursor: pointer;
  color: ${theme.colors.text.primary};
  font-size: 0.9375rem;
`;

const HeartbeatSettings = () => {
  const [preferences, setPreferences] = useState(null);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [prefsRes, vesselsRes] = await Promise.all([
        api.get('/api/heartbeat-preferences'),
        api.get('/api/vessels'),
      ]);
      const p = prefsRes.data.preferences || {};
      setPreferences({
        enabled: p.enabled ?? false,
        interval_minutes: p.interval_minutes ?? (p.interval_hours ? p.interval_hours * 60 : 720),
        email_alerts: p.email_alerts ?? true,
        vessel_ids: p.vessel_ids ?? null,
      });
      setVessels(vesselsRes.data?.vessels || []);
    } catch (err) {
      console.error('Error loading heartbeat preferences:', err);
      setPreferences({ enabled: false, interval_minutes: 720, email_alerts: true, vessel_ids: null });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/api/heartbeat-preferences', {
        enabled: preferences.enabled,
        interval_minutes: preferences.interval_minutes,
        email_alerts: preferences.email_alerts,
        vessel_ids: preferences.vessel_ids && preferences.vessel_ids.length > 0 ? preferences.vessel_ids : null,
      });
      toast.success('Boat status heartbeat settings saved!');
    } catch (err) {
      console.error('Error saving heartbeat preferences:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field) => {
    setPreferences((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleVesselToggle = (vesselId) => {
    setPreferences((prev) => {
      const ids = prev.vessel_ids || [];
      const isAllMode = ids.length === 0;
      let next;
      if (isAllMode) {
        next = vessels.filter((v) => v.id !== vesselId).map((v) => v.id);
      } else {
        next = ids.includes(vesselId) ? ids.filter((id) => id !== vesselId) : [...ids, vesselId];
      }
      return { ...prev, vessel_ids: next.length > 0 ? next : null };
    });
  };

  const isVesselChecked = (vesselId) => {
    const ids = preferences.vessel_ids || [];
    if (ids.length === 0) return true;
    return ids.includes(vesselId);
  };

  if (loading || !preferences) {
    return (
      <Container>
        <Section>
          <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
            Loading...
          </div>
        </Section>
      </Container>
    );
  }

  return (
    <Container>
      <Section>
        <SectionHeader>
          <HeartIcon style={{ width: '2rem', height: '2rem', color: theme.colors.primary.main }} />
          <div>
            <SectionTitle>Boat Status Heartbeat</SectionTitle>
            <SectionDescription>
              AI periodically checks your boat&apos;s status (battery, engine, data freshness) and emails you if there are issues.
            </SectionDescription>
          </div>
        </SectionHeader>

        <SettingItem>
          <SettingHeader $hasDescription>
            <SettingLabel htmlFor="heartbeat_enabled">
              <HeartIcon />
              Enable heartbeat
            </SettingLabel>
            <Toggle $checked={preferences.enabled}>
              <input
                type="checkbox"
                id="heartbeat_enabled"
                checked={preferences.enabled}
                onChange={() => handleToggle('enabled')}
              />
              <span />
            </Toggle>
          </SettingHeader>
          <SettingDescription>
            When enabled, AI will review your vessel data every few hours and notify you if anything looks wrong.
          </SettingDescription>
        </SettingItem>

        <SettingItem>
          <SettingHeader $hasDescription>
            <SettingLabel>Check interval</SettingLabel>
            <Select
              value={preferences.interval_minutes ?? 720}
              onChange={(e) => setPreferences((p) => ({ ...p, interval_minutes: Number(e.target.value) }))}
            >
              <option value={15}>Every 15 minutes</option>
              <option value={30}>Every 30 minutes</option>
              <option value={60}>Every 1 hour</option>
              <option value={120}>Every 2 hours</option>
              <option value={360}>Every 6 hours</option>
              <option value={720}>Every 12 hours</option>
              <option value={1440}>Every 24 hours</option>
            </Select>
          </SettingHeader>
          <SettingDescription>
            How often the AI checks your boat&apos;s status. When an issue is found, it checks every minute for 10 minutes to see if it persists, then returns to this schedule.
          </SettingDescription>
        </SettingItem>

        <SettingItem>
          <SettingHeader $hasDescription>
            <SettingLabel htmlFor="heartbeat_email">
              <HeartIcon />
              Email alerts
            </SettingLabel>
            <Toggle $checked={preferences.email_alerts}>
              <input
                type="checkbox"
                id="heartbeat_email"
                checked={preferences.email_alerts}
                onChange={() => handleToggle('email_alerts')}
              />
              <span />
            </Toggle>
          </SettingHeader>
          <SettingDescription>
            Receive an email when the AI detects issues (low battery, high temp, stale data, OnGuard alarm, etc.).
          </SettingDescription>
        </SettingItem>

        {vessels.length > 1 && (
          <SettingItem>
            <SettingHeader $hasDescription>
              <SettingLabel>Vessels to monitor</SettingLabel>
            </SettingHeader>
            <SettingDescription>
              Leave all unchecked to monitor all your boats. Or select specific vessels.
            </SettingDescription>
            <div style={{ marginTop: '0.75rem' }}>
              {vessels.map((v) => (
                <VesselCheckbox key={v.id}>
                  <input
                    type="checkbox"
                    checked={isVesselChecked(v.id)}
                    onChange={() => handleVesselToggle(v.id)}
                  />
                  {v.vessel_name}
                </VesselCheckbox>
              ))}
            </div>
          </SettingItem>
        )}

        <InfoBox>
          <h4>How it works</h4>
          <p>
            The AI reviews your vessel&apos;s SignalK data (battery, engine, tanks, etc.) and trip history. If it finds
            an issue, you&apos;ll get an email and the heartbeat switches to <strong>follow-up mode</strong>: it checks
            every minute for 10 minutes to see if the problem persists. If it clears, it returns to your normal schedule.
            You can also chat with your boat anytime at <Link to="/chat">boatwise.ca/chat</Link>.
          </p>
        </InfoBox>

        <ButtonGroup>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </ButtonGroup>
      </Section>
    </Container>
  );
};

export default HeartbeatSettings;
